import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hands-free voice session backed by OpenAI Realtime (server-side proxy).
 *
 * The browser captures mic at 24 kHz mono PCM16 and streams it via WebSocket.
 * The server (stark-voice) proxies to OpenAI Realtime which does VAD, STT,
 * LLM, tool calls, and TTS in a single pipeline. Audio comes back as PCM16
 * 24 kHz chunks; we play them back-to-back through Web Audio for low-latency
 * streaming.
 *
 * Barge-in: the server emits `tts_interrupt` when OpenAI's VAD detects the
 * user starting to talk during playback. We stop all scheduled sources.
 */

export type VoiceState =
  | 'closed'
  | 'connecting'
  | 'idle'        // mic open, listening passively
  | 'listening'   // VAD detected speech
  | 'thinking'    // model is generating
  | 'speaking'    // TTS streaming
  | 'error'

export interface UseVoiceSessionOptions {
  getToken: () => Promise<string | null>
  sessionId?: string | null
  voiceWsUrl?: string
  onTranscript?: (text: string) => void
  onResponse?: (text: string) => void
  onError?: (msg: string) => void
}

export interface UseVoiceSessionResult {
  state: VoiceState
  error: string | null
  open: () => Promise<void>
  close: () => void
}

const REALTIME_RATE = 24000

function defaultWsUrl(): string {
  if (typeof window === 'undefined') return ''
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/voice/ws`
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  const STEP = 0x8000
  for (let i = 0; i < bytes.length; i += STEP) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + STEP) as unknown as number[])
  }
  return btoa(s)
}

function base64ToInt16(b64: string): Int16Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2)
}

export function useVoiceSession(opts: UseVoiceSessionOptions): UseVoiceSessionResult {
  const [state, setState] = useState<VoiceState>('closed')
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)

  // Capture
  const captureCtxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)

  // Streaming playback: schedule each PCM16 chunk back-to-back.
  const playbackCtxRef = useRef<AudioContext | null>(null)
  const playbackHeadRef = useRef<number>(0)
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())

  const optsRef = useRef(opts)
  useEffect(() => {
    optsRef.current = opts
  }, [opts])

  const cleanupCapture = useCallback(() => {
    workletRef.current?.disconnect()
    workletRef.current = null
    sourceRef.current?.disconnect()
    sourceRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (captureCtxRef.current && captureCtxRef.current.state !== 'closed') {
      void captureCtxRef.current.close()
    }
    captureCtxRef.current = null
  }, [])

  const stopPlayback = useCallback(() => {
    for (const src of activeSourcesRef.current) {
      try {
        src.stop()
      } catch {
        // already stopped
      }
    }
    activeSourcesRef.current.clear()
    playbackHeadRef.current = 0
  }, [])

  const cleanupPlayback = useCallback(() => {
    stopPlayback()
    if (playbackCtxRef.current && playbackCtxRef.current.state !== 'closed') {
      void playbackCtxRef.current.close()
    }
    playbackCtxRef.current = null
  }, [stopPlayback])

  const close = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        // ignore
      }
      wsRef.current = null
    }
    cleanupCapture()
    cleanupPlayback()
    setState('closed')
  }, [cleanupCapture, cleanupPlayback])

  const ensurePlaybackCtx = useCallback((): AudioContext => {
    if (playbackCtxRef.current && playbackCtxRef.current.state !== 'closed') {
      return playbackCtxRef.current
    }
    // Realtime emits PCM16 24 kHz; create the playback context at the same
    // rate so AudioBuffer playback is bit-exact (no resampling step).
    const ctx = new AudioContext({ sampleRate: REALTIME_RATE })
    playbackCtxRef.current = ctx
    playbackHeadRef.current = 0
    return ctx
  }, [])

  /** Schedule one PCM16 chunk at the current playback head, advancing the head. */
  const enqueuePcm16Chunk = useCallback((b64: string) => {
    const int16 = base64ToInt16(b64)
    if (int16.length === 0) return
    const ctx = ensurePlaybackCtx()
    const buffer = ctx.createBuffer(1, int16.length, REALTIME_RATE)
    const ch = buffer.getChannelData(0)
    for (let i = 0; i < int16.length; i++) ch[i] = (int16[i] ?? 0) / 32768
    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.connect(ctx.destination)
    const startAt = Math.max(playbackHeadRef.current, ctx.currentTime + 0.02)
    src.start(startAt)
    activeSourcesRef.current.add(src)
    src.onended = () => {
      activeSourcesRef.current.delete(src)
    }
    playbackHeadRef.current = startAt + buffer.duration
  }, [ensurePlaybackCtx])

  const startMicStream = useCallback(async (): Promise<boolean> => {
    let stream: MediaStream
    try {
      // Use the browser's default constraints. Earlier attempts to force
      // autoGainControl/noiseSuppression off broke Windows/Edge, which then
      // delivered pure-zero buffers. We compensate the AGC's quiet output
      // server-side with a pre-VAD gain instead.
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'micro inaccessible')
      setState('error')
      return false
    }
    streamRef.current = stream

    // Use the device's native sample rate (worklet downsamples to 24 kHz).
    // Forcing sampleRate:24000 in the constructor triggers Chrome's input
    // resampler which has been observed to deliver zero-filled buffers.
    const ctx = new AudioContext()
    captureCtxRef.current = ctx
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch {
        // ignore
      }
    }
    try {
      await ctx.audioWorklet.addModule('/audio-capture.worklet.js')
    } catch (e) {
      cleanupCapture()
      setError('Worklet audio indisponible')
      console.error('[voice] worklet load:', e)
      setState('error')
      return false
    }
    const source = ctx.createMediaStreamSource(stream)
    sourceRef.current = source
    const worklet = new AudioWorkletNode(ctx, 'audio-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    })
    workletRef.current = worklet
    worklet.port.onmessage = (ev) => {
      const buf = ev.data as ArrayBuffer
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
      try {
        wsRef.current.send(JSON.stringify({ type: 'audio', data: bufferToBase64(buf) }))
      } catch (err) {
        console.warn('[voice] ws send failed:', err)
      }
    }
    source.connect(worklet)
    // Sink the worklet through a 0-gain to destination so Chrome actually
    // pulls audio through the graph (otherwise the source delivers silence).
    const sinkGain = ctx.createGain()
    sinkGain.gain.value = 0
    worklet.connect(sinkGain)
    sinkGain.connect(ctx.destination)
    return true
  }, [cleanupCapture])

  const open = useCallback(async () => {
    if (wsRef.current) return
    setError(null)
    setState('connecting')
    const token = await optsRef.current.getToken()
    if (!token) {
      setState('error')
      setError('Pas de session active')
      optsRef.current.onError?.('no token')
      return
    }
    const url = new URL(optsRef.current.voiceWsUrl ?? defaultWsUrl())
    url.searchParams.set('jwt', token)
    if (optsRef.current.sessionId) url.searchParams.set('session_id', optsRef.current.sessionId)

    const ws = new WebSocket(url.toString())
    wsRef.current = ws

    ws.onmessage = (ev) => {
      let msg: { type: string; [k: string]: unknown }
      try {
        msg = JSON.parse(ev.data as string)
      } catch {
        return
      }
      switch (msg.type) {
        case 'ready':
          void startMicStream().then((ok) => {
            if (ok) setState('idle')
          })
          break
        case 'speech_start':
          setState('listening')
          break
        case 'speech_end':
          // Server begins LLM/TTS pipeline.
          break
        case 'tts_interrupt':
          stopPlayback()
          break
        case 'transcript':
          if (typeof msg.text === 'string') optsRef.current.onTranscript?.(msg.text)
          break
        case 'tts_start':
          setState('speaking')
          break
        case 'tts_chunk':
          if (typeof msg.data === 'string') enqueuePcm16Chunk(msg.data)
          break
        case 'response':
          if (typeof msg.text === 'string') optsRef.current.onResponse?.(msg.text)
          break
        case 'tts_end':
          // Wait for scheduled buffers to drain, then drop back to idle.
          // playbackHead is the absolute end time of the last scheduled chunk.
          {
            const ctx = playbackCtxRef.current
            if (!ctx) {
              setState((s) => (s === 'speaking' ? 'idle' : s))
            } else {
              const wait = Math.max(0, (playbackHeadRef.current - ctx.currentTime) * 1000 + 50)
              window.setTimeout(() => {
                setState((s) => (s === 'speaking' ? 'idle' : s))
              }, wait)
            }
          }
          break
        case 'error':
          setError(typeof msg.message === 'string' ? msg.message : 'erreur')
          optsRef.current.onError?.(typeof msg.message === 'string' ? msg.message : 'erreur')
          setState((s) => (s === 'closed' ? s : 'idle'))
          break
      }
    }
    ws.onerror = () => {
      setError('Connexion voix interrompue')
      setState('error')
      optsRef.current.onError?.('ws error')
    }
    ws.onclose = (ev) => {
      wsRef.current = null
      cleanupCapture()
      cleanupPlayback()
      if (ev.code === 4401) {
        setError('Authentification voix refusée')
        setState('error')
      } else if (ev.code === 4403) {
        setError('Origine non autorisée')
        setState('error')
      } else if (ev.code === 4503) {
        setError('OpenAI non configuré')
        setState('error')
      } else {
        setState('closed')
      }
    }
  }, [cleanupCapture, cleanupPlayback, enqueuePcm16Chunk, startMicStream, stopPlayback])

  useEffect(() => {
    const onPageHide = () => close()
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      close()
    }
  }, [close])

  return { state, error, open, close }
}
